import React, { Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';

import { LoadingIndicator } from './components/atoms/loading-indicator';
import { AdminRoute } from './components/molecules/admin-route';
import { AuthenticatedRoute } from './components/molecules/authenticated-route';
import { OnboardedRoute } from './components/molecules/onboarded-route';
import { OnboardingRoute } from './components/molecules/onboarding-route';
import { MainLayout } from './layouts/main';
import { AboutPage } from './pages/about';
import { BlogPage } from './pages/blog';
import { BlogPostPage } from './pages/blog/blog-post-page';
import { LandingPage } from './pages/landing';
import { PrivacyPolicyPage } from './pages/legal/privacy-policy';
import { ReferralTermsPage } from './pages/legal/referral-terms';
import { TermsOfServicePage } from './pages/legal/terms-of-service';
import { UserDataDeletionPage } from './pages/legal/user-data-deletion';
import { ResetPasswordPage } from './pages/reset-password';
import { UnsubscribePage } from './pages/unsubscribe';
import lazyWithRetry from './utils/lazy-with-retry';

const DashboardLayout = lazyWithRetry(() => import('./layouts/dashboard/DashboardLayout'));
const AdminLayout = lazyWithRetry(() => import('./layouts/admin/AdminLayout'));
const OnboardingLayout = lazyWithRetry(() => import('./layouts/onboarding/OnboardingLayout'));

const NotFoundPage = lazyWithRetry(() => import('./pages/not-found/NotFoundPage'));
const PricingPage = lazyWithRetry(() => import('./pages/pricing/PricingPage'));
const OnboardingPage = lazyWithRetry(() => import('./pages/onboarding/OnboardingPage'));
const PlanGenerationPage = lazyWithRetry(() => import('./pages/onboarding/plan-generation-page/PlanGenerationPage'));

const UserDashboardPage = lazyWithRetry(() => import('./pages/user/user-dashboard-page/UserDashboardPage'));
const UserProfilePage = lazyWithRetry(() => import('./pages/user/user-profile/UserProfilePage'));
const UserWorkoutsPage = lazyWithRetry(() => import('./pages/user/user-workouts/UserWorkoutsPage'));
const WorkoutDetails = lazyWithRetry(() => import('./pages/user/user-workouts/details/WorkoutDetails'));
const UserRecipesPage = lazyWithRetry(() => import('./pages/user/user-recipes-page/UserRecipesPage'));
const UserRecipeDetailsPage = lazyWithRetry(
  () => import('./pages/user/user-recipe-details-page/UserRecipeDetailsPage'),
);
const UserTrackingPage = lazyWithRetry(() => import('./pages/user/user-tracking-page/UserTrackingPage'));
const GettingStartedPage = lazyWithRetry(() => import('./pages/user/getting-started-page/GettingStartedPage'));
const UserTrackingHistoryPage = lazyWithRetry(
  () => import('./pages/user/user-tracking-history-page/UserTrackingHistoryPage'),
);
const UserFastingPage = lazyWithRetry(() => import('./pages/user/user-fasting-page/UserFastingPage'));
const UserGoalsPage = lazyWithRetry(() => import('./pages/user/user-goals-page/UserGoalsPage'));
const UserInsightsPage = lazyWithRetry(() => import('./pages/user/user-insights-page/UserInsightsPage'));
const UserPlanPage = lazyWithRetry(() => import('./pages/user/user-plan-page/UserPlanPage'));
const UserSettingsPage = lazyWithRetry(() => import('./pages/user/user-settings/UserSettingsPage'));
const UserHelpPage = lazyWithRetry(() => import('./pages/user/user-help/UserHelpPage'));
const UserPaymentMethodsPage = lazyWithRetry(
  () => import('./pages/user/user-payment-methods-page/UserPaymentMethodsPage'),
);
const UserSubscriptionPage = lazyWithRetry(() => import('./pages/user/user-subscription-page/UserSubscriptionPage'));
const UserReferralsPage = lazyWithRetry(() => import('./pages/user/user-referrals-page/UserReferralsPage'));

const AdminAnalyticsPage = lazyWithRetry(() => import('./pages/admin/admin-analytics-page/AdminAnalyticsPage'));
const AdminEngagementPage = lazyWithRetry(() => import('./pages/admin/admin-engagement-page/AdminEngagementPage'));
const AdminPostsPage = lazyWithRetry(() => import('./pages/admin/admin-posts-page/AdminPostsPage'));
const AdminUsersPage = lazyWithRetry(() => import('./pages/admin/admin-users-page/AdminUsersPage'));
const AdminWorkoutsPage = lazyWithRetry(() => import('./pages/admin/admin-workouts-page/AdminWorkoutsPage'));
const AdminHelpPage = lazyWithRetry(() => import('./pages/admin/admin-help-page/AdminHelpPage'));
const AdminPricingPlansPage = lazyWithRetry(
  () => import('./pages/admin/admin-pricing-plans-page/AdminPricingPlansPage'),
);
const ImpersonatePage = lazyWithRetry(() => import('./pages/admin/impersonate-page/ImpersonatePage'));

// SEO pages — calculators
const BmiCalculatorPage = lazyWithRetry(() => import('./pages/bmi-calculator/BmiCalculatorPage'));
const TdeeCalculatorPage = lazyWithRetry(() => import('./pages/tdee-calculator/TdeeCalculatorPage'));
const MacroCalculatorPage = lazyWithRetry(() => import('./pages/macro-calculator/MacroCalculatorPage'));
const CalorieDeficitCalculatorPage = lazyWithRetry(
  () => import('./pages/calorie-deficit-calculator/CalorieDeficitCalculatorPage'),
);
const ProteinCalculatorPage = lazyWithRetry(() => import('./pages/protein-calculator/ProteinCalculatorPage'));
const WaterIntakeCalculatorPage = lazyWithRetry(
  () => import('./pages/water-intake-calculator/WaterIntakeCalculatorPage'),
);

// SEO pages — features
const AiFoodScannerPage = lazyWithRetry(() => import('./pages/ai-food-scanner/AiFoodScannerPage'));
const AiWorkoutPlannerPage = lazyWithRetry(() => import('./pages/ai-workout-planner/AiWorkoutPlannerPage'));
const FastingTrackerPage = lazyWithRetry(() => import('./pages/fasting-tracker/FastingTrackerPage'));
const MealPlannerPage = lazyWithRetry(() => import('./pages/meal-planner/MealPlannerPage'));
const MacroTrackerPage = lazyWithRetry(() => import('./pages/macro-tracker/MacroTrackerPage'));
const AiInsightsPage = lazyWithRetry(() => import('./pages/ai-insights/AiInsightsPage'));

// SEO pages — educational
const FastingStagesPage = lazyWithRetry(() => import('./pages/fasting-stages/FastingStagesPage'));
const FastingBenefitsPage = lazyWithRetry(() => import('./pages/fasting-benefits/FastingBenefitsPage'));
const IntermittentFastingGuidePage = lazyWithRetry(
  () => import('./pages/intermittent-fasting-guide/IntermittentFastingGuidePage'),
);
const AutophagyTimelinePage = lazyWithRetry(() => import('./pages/autophagy-timeline/AutophagyTimelinePage'));

const CapturePageTemplate = lazyWithRetry(() => import('./pages/capture/CapturePageTemplate'));

const SuspenseFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <LoadingIndicator />
  </div>
);

const MainRoutes = () => (
  <Suspense fallback={<SuspenseFallback />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="start/:segment" element={<CapturePageTemplate />} />
      <Route element={<MainLayout />}>
        <Route path="about" element={<AboutPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="unsubscribe" element={<UnsubscribePage />} />
      </Route>
      <Route element={<MainLayout showHeader={false} />}>
        <Route path="pricing" element={<PricingPage />} />
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="referral-terms" element={<ReferralTermsPage />} />
        <Route path="terms-of-service" element={<TermsOfServicePage />} />
        <Route path="user-data-deletion" element={<UserDataDeletionPage />} />
        {/* SEO pages — calculators */}
        <Route path="bmi-calculator" element={<BmiCalculatorPage />} />
        <Route path="tdee-calculator" element={<TdeeCalculatorPage />} />
        <Route path="macro-calculator" element={<MacroCalculatorPage />} />
        <Route path="calorie-deficit-calculator" element={<CalorieDeficitCalculatorPage />} />
        <Route path="protein-calculator" element={<ProteinCalculatorPage />} />
        <Route path="water-intake-calculator" element={<WaterIntakeCalculatorPage />} />
        {/* SEO pages — features */}
        <Route path="ai-food-scanner" element={<AiFoodScannerPage />} />
        <Route path="ai-workout-planner" element={<AiWorkoutPlannerPage />} />
        <Route path="fasting-tracker" element={<FastingTrackerPage />} />
        <Route path="meal-planner" element={<MealPlannerPage />} />
        <Route path="macro-tracker" element={<MacroTrackerPage />} />
        <Route path="ai-insights" element={<AiInsightsPage />} />
        {/* SEO pages — educational */}
        <Route path="fasting-stages" element={<FastingStagesPage />} />
        <Route path="fasting-benefits" element={<FastingBenefitsPage />} />
        <Route path="intermittent-fasting-guide" element={<IntermittentFastingGuidePage />} />
        <Route path="autophagy-timeline" element={<AutophagyTimelinePage />} />
      </Route>
      <Route element={<DashboardLayout />}>
        {/* User dashboard - requires authentication + completed onboarding */}
        <Route path="user" element={<OnboardedRoute />}>
          <Route index element={<UserDashboardPage />} />
          <Route path="getting-started" element={<GettingStartedPage />} />
          <Route path="dashboard" element={<UserDashboardPage />} />
          <Route path="profile" element={<UserProfilePage />} />
          <Route path="workouts" element={<UserWorkoutsPage />} />
          <Route path="workouts/:id" element={<WorkoutDetails />} />
          <Route path="recipes" element={<UserRecipesPage />} />
          <Route path="recipes/:id" element={<UserRecipeDetailsPage />} />
          <Route path="tracking" element={<UserTrackingPage />} />
          <Route path="tracking/history" element={<UserTrackingHistoryPage />} />
          <Route path="fasting" element={<UserFastingPage />} />
          <Route path="goals" element={<UserGoalsPage />} />
          <Route path="insights" element={<UserInsightsPage />} />
          <Route path="plan" element={<UserPlanPage />} />
          <Route path="settings" element={<UserSettingsPage />} />
          <Route path="help" element={<UserHelpPage />} />
          <Route path="payment-methods" element={<UserPaymentMethodsPage />} />
          <Route path="subscription" element={<UserSubscriptionPage />} />
          <Route path="referrals" element={<UserReferralsPage />} />
        </Route>
      </Route>
      <Route element={<OnboardingLayout />}>
        {/* Onboarding - just needs auth, no subscription required */}
        <Route path="onboarding" element={<OnboardingRoute />}>
          <Route index element={<OnboardingPage />} />
        </Route>
        <Route element={<AuthenticatedRoute />}>
          <Route path="plan-generation" element={<PlanGenerationPage />} />
        </Route>
      </Route>
      <Route element={<AdminLayout />}>
        <Route path="admin" element={<AdminRoute />}>
          <Route index element={<AdminAnalyticsPage />} />
          <Route path="engagement" element={<AdminEngagementPage />} />
          <Route path="posts" element={<AdminPostsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="workouts" element={<AdminWorkoutsPage />} />
          <Route path="pricing-plans" element={<AdminPricingPlansPage />} />
          <Route path="help" element={<AdminHelpPage />} />
        </Route>
      </Route>
      <Route path="impersonate" element={<ImpersonatePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

export default MainRoutes;
